import React from 'react';
import { getWebSearchProviderLabel } from '../../utils/webSearchProviders';

export function ActiveUserSelectionsPanel({ p }) {
  const {
    userSettingsLoading,
    saving,
    getActiveModelLabel,
    getActiveWebSearchLabel,
    getProviderDisplayName,
    configuredLlmProviders,
    configuredWebSearchProviders,
    canConfirmActiveModelSelection,
    showActiveModelPicker,
    showActiveWebSearchPicker,
    setShowActiveWebSearchPicker,
    showActiveModelProviderWarning,
    pendingActiveModel,
    draftActiveModelProvider,
    draftActiveModelId,
    draftActiveWebSearchProvider,
    draftProviderModels,
    draftProviderModelsLoading,
    draftProviderModelsError,
    setDraftActiveModelId,
    setDraftActiveWebSearchProvider,
    handleOpenActiveModelPicker,
    handleCloseActiveModelPicker,
    handleOpenActiveWebSearchPicker,
    handleConfirmActiveModelSelection,
    handleConfirmActiveWebSearchSelection,
    handleConfirmActiveModelProviderWarning,
    handleCancelActiveModelProviderWarning,
    handleDraftActiveModelProviderChange,
    isActiveWebSearchDraftSelected,
  } = p;

  const providerChoices = configuredLlmProviders.map((provider) => ({
    providerSlug: provider.slug,
    providerName: provider.name || getProviderDisplayName(provider),
  }));

  const selectedProviderLabel = providerChoices.find(
    (group) => group.providerSlug === draftActiveModelProvider
  )?.providerName || draftActiveModelProvider;

  const modelStepState = !draftActiveModelProvider
    ? 'pending'
    : draftProviderModelsLoading
      ? 'loading'
      : draftActiveModelId
        ? 'complete'
        : 'current';

  const showCustomModelId = !draftProviderModelsLoading
    && draftProviderModels.length === 0
    && !!draftActiveModelProvider
    && !draftProviderModelsError;

  return (
    <>
      <div className="settings-category" id="user-active-model-settings">
        <h3>
          <i className="fas fa-robot"></i> Active Model
          <span className="badge badge-user">USER</span>
        </h3>
        <div className="setting-item">
          <div>
            <div className="setting-label">Default chat model</div>
            <div className="setting-description">
              Choose a provider, then pick a model. This applies to chat and tool calls across projects.
            </div>
          </div>
          <div className="setting-value active-selection-value">
            <div className="active-selection-current">
              {userSettingsLoading ? 'Loading…' : getActiveModelLabel()}
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleOpenActiveModelPicker}
              disabled={saving || userSettingsLoading || configuredLlmProviders.length === 0}
            >
              <i className="fas fa-list"></i> Choose active model
            </button>
          </div>
        </div>
        {configuredLlmProviders.length === 0 && !userSettingsLoading && (
          <div className="setting-description" style={{ marginTop: 8, color: '#9ca3af' }}>
            Add at least one LLM API key below to select an active model.
          </div>
        )}
      </div>

      <div className="settings-category" id="user-active-web-search-settings">
        <h3>
          <i className="fas fa-magnifying-glass"></i> Active Web Search
          <span className="badge badge-user">USER</span>
        </h3>
        <div className="setting-item">
          <div>
            <div className="setting-label">Default web search</div>
            <div className="setting-description">
              Choose which web search provider to use when tools need search results.
            </div>
          </div>
          <div className="setting-value active-selection-value">
            <div className="active-selection-current">
              {userSettingsLoading ? 'Loading…' : getActiveWebSearchLabel()}
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleOpenActiveWebSearchPicker}
              disabled={saving || userSettingsLoading || configuredWebSearchProviders.length === 0}
            >
              <i className="fas fa-list"></i> Choose active web search
            </button>
          </div>
        </div>
        {configuredWebSearchProviders.length === 0 && !userSettingsLoading && (
          <div className="setting-description" style={{ marginTop: 8, color: '#9ca3af' }}>
            Add a web search API key or enable &quot;I feel lucky!&quot; below to choose a default.
          </div>
        )}
      </div>

      {showActiveModelPicker && (
        <div className="modal show">
          <div className="modal-content active-selection-modal">
            <div className="modal-header">
              <h2 className="modal-title">Choose active model</h2>
              <span className="close" onClick={handleCloseActiveModelPicker}>&times;</span>
            </div>
            <div className="modal-body">
              {providerChoices.length === 0 ? (
                <div className="active-selection-empty">
                  No providers with saved keys were found.
                </div>
              ) : (
                <div className="active-model-wizard">
                  <ol className="active-model-steps" aria-label="Model selection steps">
                    <li className={`active-model-step ${draftActiveModelProvider ? 'is-complete' : 'is-current'}`}>
                      <span className="active-model-step-marker" aria-hidden="true">1</span>
                      <span className="active-model-step-label">Choose provider</span>
                    </li>
                    <li className={`active-model-step is-${modelStepState}`}>
                      <span className="active-model-step-marker" aria-hidden="true">2</span>
                      <span className="active-model-step-label">Choose model</span>
                    </li>
                  </ol>

                  <div className="active-model-step-panel">
                    <div className="setting-label">Provider</div>
                    <div className="setting-description">
                      Pick which AI provider to use (OpenAI, Pansynapse, etc.).
                    </div>
                    <select
                      className="form-control"
                      value={draftActiveModelProvider || ''}
                      onChange={(e) => handleDraftActiveModelProviderChange(e.target.value)}
                    >
                      <option value="">Select a provider…</option>
                      {providerChoices.map((group) => (
                        <option key={group.providerSlug} value={group.providerSlug}>
                          {group.providerName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {draftActiveModelProvider && (
                    <div className="active-model-step-panel">
                      <div className="setting-label">Model</div>
                      <div className="setting-description">
                        Models are loaded from the provider API (or registry for internal providers like Pansynapse).
                      </div>

                      {draftProviderModelsLoading ? (
                        <div className="active-model-loading-row" role="status" aria-live="polite">
                          <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                          <span>Loading models for {selectedProviderLabel}…</span>
                        </div>
                      ) : draftProviderModels.length > 0 ? (
                        <select
                          className="form-control"
                          value={draftActiveModelId || ''}
                          onChange={(e) => setDraftActiveModelId(e.target.value)}
                        >
                          <option value="">Select a model…</option>
                          {draftProviderModels.map((model) => (
                            <option key={model.id} value={model.id}>
                              {model.name && model.name !== model.id
                                ? `${model.name} (${model.id})`
                                : model.id}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="active-selection-empty">
                          No models returned for this provider.
                        </div>
                      )}

                      {draftProviderModelsError && !draftProviderModelsLoading && (
                        <div className="active-model-step-error">
                          {draftProviderModelsError}
                        </div>
                      )}
                    </div>
                  )}

                  {showCustomModelId && (
                    <div className="active-model-step-panel">
                      <div className="setting-label">Model ID</div>
                      <div className="setting-description">
                        Enter a model ID manually if the provider did not return a list.
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. gpt-4o-mini"
                        value={draftActiveModelId}
                        onChange={(e) => setDraftActiveModelId(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCloseActiveModelPicker}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmActiveModelSelection}
                disabled={!canConfirmActiveModelSelection}
              >
                {saving || draftProviderModelsLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    {' '}{saving ? 'Saving…' : 'Loading…'}
                  </>
                ) : (
                  'Save selection'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showActiveWebSearchPicker && (
        <div className="modal show">
          <div className="modal-content active-selection-modal">
            <div className="modal-header">
              <h2 className="modal-title">Choose active web search</h2>
              <span className="close" onClick={() => setShowActiveWebSearchPicker(false)}>&times;</span>
            </div>
            <div className="modal-body">
              <div className="active-selection-list">
                {configuredWebSearchProviders.map((provider) => {
                  const selected = isActiveWebSearchDraftSelected(provider.id);
                  return (
                    <label className={`active-selection-option ${selected ? 'selected' : ''}`} key={provider.id}>
                      <input
                        type="radio"
                        name="active-web-search"
                        checked={selected}
                        onChange={() => setDraftActiveWebSearchProvider(provider.id)}
                      />
                      <span className="active-selection-option-label">
                        <span className="active-selection-option-name">{provider.name}</span>
                        <span className="active-selection-option-meta">{provider.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowActiveWebSearchPicker(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmActiveWebSearchSelection}
                disabled={!draftActiveWebSearchProvider || saving}
              >
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    {' '}Saving…
                  </>
                ) : (
                  'Save selection'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showActiveModelProviderWarning && pendingActiveModel && (
        <div className="modal show">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Change active AI provider?</h2>
              <span className="close" onClick={handleCancelActiveModelProviderWarning}>&times;</span>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
                <i className="fas fa-exclamation-triangle"></i>
                {' '}
                Switching to a different AI provider will change which embedding model is used for vector search.
                Existing embeddings may need to be recalculated before search quality returns to normal.
              </div>
              <p>
                Change active model to{' '}
                <strong>
                  {pendingActiveModel.providerName}
                  {pendingActiveModel.modelId ? (
                    <> · {pendingActiveModel.modelName || pendingActiveModel.modelId}</>
                  ) : null}
                </strong>
                ?
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={handleCancelActiveModelProviderWarning}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleConfirmActiveModelProviderWarning} disabled={saving}>
                {saving ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    {' '}Saving…
                  </>
                ) : (
                  'Change provider'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function getActiveWebSearchDisplayLabel(providerId) {
  if (!providerId) return 'Not selected';
  return getWebSearchProviderLabel(providerId);
}
